import { hideScrollbar } from '@noths/polaris-client-styles';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { colors, spacing, typography } = themeVariables;

export const header = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(${spacing.sm});
  margin-bottom: var(${spacing.sm});
`;

/* The heading and its link stack, so a long "See all women's gifts" wraps under
   the heading instead of squeezing it. */
export const headingGroup = css`
  display: flex;
  flex-direction: column;
  gap: calc(var(${spacing.xxs}) / 2);
  min-width: 0;
`;

export const heading = css`
  margin: 0;
  font-family: var(${typography.heading.fontFamily});
  font-weight: var(${typography.heading.fontWeight});
  font-size: var(${typography.heading.sizes.sm.fontSize});
  line-height: var(${typography.heading.sizes.sm.lineHeight});
  letter-spacing: var(${typography.heading.letterSpacing});
  color: var(${colors.content.default});
`;

export const subheadingLink = css`
  align-self: flex-start;
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  color: var(${colors.content.link});

  &:focus-visible {
    outline: 2px solid var(${colors.stroke.focus});
    outline-offset: 2px;
  }
`;

/* IconButton takes no className and its own css prop is spoken for, so the two
   arrows are positioned by this wrapper rather than by the buttons themselves.
   Never shrinks: the heading gives way first.

   Shown only where the device has no touchscreen, since the arrows stand in for a
   swipe rather than accompanying one. */
export const controls = css`
  display: flex;
  flex-shrink: 0;
  gap: var(${spacing.xs});

  /* A touchscreen swipes the track directly, so there the arrows are redundant
     furniture. The Ribbons carousel said the same thing with a 480px breakpoint.

     Asked as a pointer capability rather than a width, so a tablet loses them at
     any size and a narrow desktop window keeps them. Note this is the mirror of
     the ProductCard dots rule, and deliberately so: dots indicate a swipe and so
     follow the touchscreen, arrows substitute for one and so go the other way.

     display: none rather than a visual hide, so the buttons leave the
     accessibility tree too, as they did in Ribbons where the DOM was omitted. */
  @media (any-pointer: coarse) {
    display: none;
  }
`;

/* The scroll container and the list are the same element, so the track is what
   an intersection is measured against and there is no wrapper in between.

   `overscroll-behavior-x: contain` stops a swipe that reaches the end of the
   carousel from being handed to the page behind it, which on iOS is a
   back-navigation gesture. */
export const track = css`
  /* First, because it declares an overflow-y of its own that a horizontal track
     has no use for. The pair below is the one that matters. */
  ${hideScrollbar}

  display: flex;
  gap: var(--product-carousel-gap);
  margin: 0;
  padding: 0;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  list-style: none;

  /* How wide each card is and how far apart they sit, per itemSize step. Each
     step swaps the same two properties through an attribute selector rather than
     interpolating the prop, so styled-components generates and injects a single
     rule however many carousels a page renders. md is the base declaration, and
     sm overrides it on specificity, (0,2,0) against (0,1,0), rather than on
     source order.

     Declared here rather than on the section because the track reads the gap
     itself and the items inherit the width, and because the section carries the
     ref the carousel impression depends on.

     Every step is a clamp, and the percentage is of the track rather than of the
     viewport, so a step says how big a card should be while the carousel still
     answers to the space it has been given. No step change in between, and no
     breakpoint anywhere in the component.

     The floor both steps share is 8.75rem, the narrowest width ProductCard is
     verified at. Below it the card's price row overflows and is clipped, its
     title cuts mid-word, and its partner name runs to a third line. sm's floor
     binds on a phone, unlike md's, so lowering it would ship a rendering state
     that does not occur today. Hardening the card is what would let it come
     down. */
  --product-carousel-item-width: clamp(8.75rem, 45%, 15rem);
  --product-carousel-gap: var(${spacing.sm});

  &[data-item-size='sm'] {
    --product-carousel-item-width: clamp(8.75rem, 32%, 11rem);
    --product-carousel-gap: var(${spacing.xs});
  }
`;

/* The card fills whatever it is given and takes the full height of its item, so
   constraining width here is all that is needed for a row of cards to line up.
   `align-items` is left alone on the track for the same reason: stretch is what
   makes every item as tall as the tallest.

   The width is inherited from the track, which declares it per step. No `var()`
   fallback, because the track always declares it. */
export const item = css`
  flex: 0 0 var(--product-carousel-item-width);
  scroll-snap-align: start;
`;

/* Stands in for the heading while it is loading and the consumer gave none. */
export const headingSkeleton = css`
  width: 40%;
  max-width: 12rem;
  height: var(${typography.heading.sizes.sm.lineHeight});
  border-radius: 4px;
  background-color: var(${colors.surface.disabled});
`;
